import React from 'react';
import { getSubjectsForLevel } from '@tutor/types';
import SearchableDropdown from '../common/SearchableDropdown';
import MultiSelect from '../common/MultiSelect';
import type { ParentRegistrationFormData } from '../../types';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface SubjectDurationStepProps {
  formData: ParentRegistrationFormData;
  errors: Record<string, string>;
  onChange: (formData: Partial<ParentRegistrationFormData>) => void;
}

const SubjectDurationStep: React.FC<SubjectDurationStepProps> = ({
  formData,
  errors,
  onChange,
}) => {
  const { locale } = useOptionalI18n();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-1 h-5 bg-accent rounded-full" />
        <h3 className="text-lg font-semibold text-gray-900">{localizeText(locale, 'Subject & Duration')}</h3>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {localizeText(locale, 'Subject')} <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={
                formData.level
                  ? getSubjectsForLevel(formData.level).map((subject) => ({
                      value: subject,
                      label: subject,
                    }))
                  : []
              }
              value={formData.subject}
              onChange={(value) => onChange({ subject: String(value) })}
              placeholder={localizeText(locale, 'Select Subject')}
              searchable={true}
              disabled={!formData.level}
              className={errors.subject ? 'border-red-500' : ''}
            />
            {!formData.level && !errors.subject && (
              <p className="text-sm text-gray-500 mt-1">
                {localizeText(locale, 'Please select a level first')}
              </p>
            )}
            {errors.subject && (
              <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {localizeText(locale, 'Teaching Languages')} <span className="text-red-500">*</span>
            </label>
            <MultiSelect
              options={['English', 'Malay', 'Chinese', 'Tamil']}
              value={formData.teachingLanguages}
              onChange={(languages) =>
                onChange({ teachingLanguages: languages })
              }
              placeholder={localizeText(locale, 'Select languages...')}
              displayMode="count"
              searchable={false}
              allowCustom={false}
            />
            {errors.teachingLanguages && (
              <p className="text-red-500 text-sm mt-1">
                {errors.teachingLanguages}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {localizeText(locale, 'Lessons per Week')} <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={[
                { value: 1, label: localizeText(locale, '1 lesson/week') },
                { value: 2, label: localizeText(locale, '2 lessons/week') },
                { value: 3, label: localizeText(locale, '3 lessons/week') },
                { value: 4, label: localizeText(locale, '4 lessons/week') },
                { value: 5, label: localizeText(locale, '5 lessons/week') },
              ]}
              value={formData.lessonsPerWeek}
              onChange={(value) =>
                onChange({ lessonsPerWeek: parseInt(String(value)) || 0 })
              }
              placeholder={localizeText(locale, 'Select')}
              searchable={false}
              className={errors.lessonsPerWeek ? 'border-red-500' : ''}
            />
            {errors.lessonsPerWeek && (
              <p className="text-red-500 text-sm mt-1">
                {errors.lessonsPerWeek}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {localizeText(locale, 'Duration per Lesson')} <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={[
                { value: 1, label: localizeText(locale, '1 hour') },
                { value: 1.5, label: localizeText(locale, '1.5 hours') },
                { value: 2, label: localizeText(locale, '2 hours') },
                { value: 2.5, label: localizeText(locale, '2.5 hours') },
                { value: 3, label: localizeText(locale, '3 hours') },
              ]}
              value={formData.lessonDuration}
              onChange={(value) =>
                onChange({ lessonDuration: parseFloat(String(value)) || 0 })
              }
              placeholder={localizeText(locale, 'Select')}
              searchable={false}
              className={errors.lessonDuration ? 'border-red-500' : ''}
            />
            {errors.lessonDuration && (
              <p className="text-red-500 text-sm mt-1">
                {errors.lessonDuration}
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {localizeText(locale, 'Additional Remarks/Requirements')}
          </label>
          <textarea
            className="input-field h-32"
            placeholder={localizeText(locale, "E.g., student's current level, specific topics to focus on, preferred tutor characteristics...")}
            value={formData.tutorRemarks}
            onChange={(e) => onChange({ tutorRemarks: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

export default SubjectDurationStep;
